import * as React from 'react'

import {
  Box,
  Button,
  Icon,
  Link,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import {
  ProfileEmployeeAccessFragment,
  SetRegularAccessStartDateDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeStatusBannerEmployee } from 'modules/employees/components/blocks/EmployeeStatusBanner/types'
import { processesOptions } from 'modules/processes/components/WorkflowSelector'
import { useAssignableProcesses } from 'modules/processes/hooks/useAssignableProcesses'
import { useCreateMaterializeProcess } from 'modules/processes/hooks/useCreateMaterializedProcess'

const BorderBox = ({
  children,
  borderRadiusBottom = true,
}: {
  children: React.ReactNode
  borderRadiusBottom?: boolean
}) => {
  return (
    <Box
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      borderRadius={
        borderRadiusBottom ? { all: 'abs008' } : { topLeft: 'abs008', topRight: 'abs008' }
      }
      padding="s16"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="flexStart"
      gap="s16"
    >
      {children}
    </Box>
  )
}

interface Props {
  access: ProfileEmployeeAccessFragment
  employee: EmployeeStatusBannerEmployee
  setOnboardingProcessCreated: React.Dispatch<React.SetStateAction<string | undefined>>
  onClose: () => void
}

const ManageAccessModal = ({ access, employee, setOnboardingProcessCreated, onClose }: Props) => {
  const [regularAccess, setRegularAccess] = React.useState(employee.hasRegularAccess)
  const [openOboardingSelector, setOpenOnboardingSelector] = React.useState(false)
  const [selectedOnboarding, setSelectedOnboarding] = React.useState<string>()
  const { loading: loadingProcesses, processes: assignableProcesses } = useAssignableProcesses([
    'onboarding',
  ])

  const { loading: loadingCreateProcess, createMaterializedProcess } = useCreateMaterializeProcess()

  const regularAccessChanged = employee.hasRegularAccess !== regularAccess

  const [setRegularAccessStartDate, { loading }] = useMutation(SetRegularAccessStartDateDocument, {
    extractorFn: (data) => data.employeesMutations.setRegularAccessStartDateEmployeesEmployee,
    onCompleted: (data) => {
      if (data.employeesMutations.setRegularAccessStartDateEmployeesEmployee.errors.length === 0) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('employees.regular_access.modal_success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('employees.regular_access.modal_error'),
        })
      }

      onClose()
    },
  })

  const changes = regularAccessChanged || !!selectedOnboarding

  return (
    <Modal
      title={i18n.t('employees.creation.invitation_accepted.manage_access_modal.title')}
      description={i18n.t('employees.creation.invitation_accepted.manage_access_modal.description')}
      illustration="document-edit"
      onSubmit={async () => {
        if (regularAccessChanged) {
          return setRegularAccessStartDate({
            variables: {
              id: employee.id,
              startsOn: i18nDateToString(moment(), 'backend'),
            },
          })
        }

        if (selectedOnboarding) {
          const processId = Number(selectedOnboarding)

          await createMaterializedProcess({
            processId: processId,
            resourceId: access.id,
          })

          const process = assignableProcesses.find((p) => p.id === processId)
          if (process) {
            setOnboardingProcessCreated(process.name)
          }
        }
      }}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading || loadingCreateProcess || !changes} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Link.Href
        target="_blank"
        href={i18n.t('employees.creation.invitation_accepted.manage_access_modal.learn_more_link')}
        external={false}
      >
        {i18n.t('employees.creation.invitation_accepted.manage_access_modal.learn_more')}
      </Link.Href>

      {loading && <Loading />}

      {!loading && (
        <Box gap="s24" paddingTop="s24">
          <BorderBox>
            <Text.Normal weight="normal" color="grey1000">
              {i18n.t('employees.regular_access.action')}
            </Text.Normal>

            <TogglerCheckbox
              value={regularAccess}
              onChange={() => setRegularAccess(!regularAccess)}
              disabled={employee.hasRegularAccess}
            />
          </BorderBox>

          <>
            <BorderBox borderRadiusBottom={!openOboardingSelector}>
              <Text.Normal weight="normal" color="grey1000">
                {i18n.t('employee_form.wizard.onboarding_step.onboarding_workflow_selector.title')}
              </Text.Normal>

              <TogglerCheckbox
                value={openOboardingSelector}
                onChange={() => setOpenOnboardingSelector(!openOboardingSelector)}
              />
            </BorderBox>
            {openOboardingSelector && (
              <Box
                border={{
                  right: { color: 'grey400', width: 's1', style: 'solid' },
                  left: { color: 'grey400', width: 's1', style: 'solid' },
                  bottom: { color: 'grey400', width: 's1', style: 'solid' },
                }}
                borderRadius={{ bottomLeft: 'abs008', bottomRight: 'abs008' }}
                padding="s24"
                background="grey100"
              >
                {loadingProcesses ? (
                  <Loading />
                ) : (
                  <Selector.Popover
                    label={i18n.t(
                      'employee_form.wizard.onboarding_step.onboarding_workflow_selector.selector_label'
                    )}
                    blankLabel=""
                    searchPlaceholder=""
                    placeholder={i18n.t(
                      'employee_form.wizard.onboarding_step.onboarding_workflow_selector.placeholder'
                    )}
                    onChange={(value) => setSelectedOnboarding(value ? `${value}` : undefined)}
                    value={selectedOnboarding ? [+selectedOnboarding] : []}
                  >
                    {processesOptions(assignableProcesses)}
                  </Selector.Popover>
                )}
              </Box>
            )}
          </>

          {!openOboardingSelector && (
            <Box
              borderRadius={{ all: 'abs016' }}
              background="viridian100"
              padding="s12"
              paddingLeft="s16"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
            >
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="center"
                width="full"
              >
                <Box gap="s12" flexDirection="row" alignItems="center">
                  <Box background="viridian1000" borderRadius={{ all: 'abs008' }} padding="s2">
                    <Icon.Medium icon="lightbulb" color="grey000White" />
                  </Box>
                  <Text.Normal>
                    {i18n.t(
                      'employee_form.wizard.onboarding_step.onboarding_workflow_selector.helper'
                    )}
                  </Text.Normal>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}

export default observer(ManageAccessModal)
